import { SegmentedControl, Skeleton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import type { GenerationType, Job } from "../../../api/generated/model";
import { accessScopeQuery } from "../../../api/session";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { GenerationDetailModal } from "../../../platform/jobs/detail/GenerationDetailModal";
import { GENERATIONS_PAGE, useJobList, type JobFilter } from "../../../platform/jobs/job-list";
import { modelTitle } from "../../../platform/jobs/model-name";
import { useStreamStatus } from "../../../platform/stream-status";
import { formatDate } from "../../../ui/format";
import { LoadAhead, morePages } from "../../../ui/LoadAhead";
import create from "../create/create.module.css";
import home from "../pro-home.module.css";
import { ResultCard } from "../ResultCard";
import classes from "./generations.module.css";

/** The gallery's kinds, as `?kind=` names them on Pro's Generations and Simple's Library. */
export const GALLERY_KINDS = ["image", "ad-static", "video", "audio"] as const;
export type Kind = (typeof GALLERY_KINDS)[number];

/** The Sounds page's kinds, as the Voice and sound page reads them. */
const SOUND_TYPES: readonly GenerationType[] = ["audio.sfx", "audio.tts", "audio.music"];

/** Each kind's read, the one an existing page makes (pro-studio-redesign, decision 27). */
const READS: Record<Kind, Pick<JobFilter, "medium" | "types">> = {
  image: { medium: "image" },
  "ad-static": { types: ["image.advanced"] },
  video: { medium: "video" },
  audio: { types: SOUND_TYPES },
};

type Who = "mine" | "team";

/** The jobs in days, newest first, each day's in the list's order. */
function byDay(jobs: readonly Job[]): { day: string; jobs: Job[] }[] {
  const days: { day: string; jobs: Job[] }[] = [];
  for (const job of jobs) {
    const day = formatDate(job.created_at);
    const last = days.at(-1);
    if (last?.day === day) last.jobs.push(job);
    else days.push({ day, jobs: [job] });
  }
  return days;
}

export interface GenerationsGalleryProps {
  teamId: string;
  brandId: string;
  /** The kind chosen, read as its own page reads it; none is all of them. */
  kind: Kind | undefined;
  /** The page's chips, its own links (each a kind in its URL), beside Mine and Team. */
  kinds: ReactNode;
  /** A link's job (`?job=`), opened over the gallery. */
  linked: string | undefined;
  /** Its detail closed: the page drops the link's job from its URL, keeping the kind. */
  onCloseLinked: () => void;
}

/**
 * Everything the brand made, the user's or the team's, in days; a result opens the Studio's own
 * detail (spec ui-perf/pro-studio-redesign, PR d1). Pro's Generations and Simple's Library draw it
 * (spec ui-perf/simple-library), each with its own heading, chips and URL; the page keys it by its
 * team and brand, so another brand starts anew, with Mine again and no detail open.
 */
export function GenerationsGallery({ teamId, brandId, kind, kinds, linked, onCloseLinked }: GenerationsGalleryProps) {
  const filter = kind === undefined ? {} : READS[kind];
  const [who, setWho] = useState<Who>("mine");
  // A link's job (Simple's Open in Pro) opens over the page; closing it drops the link's param.
  const [open, setOpen] = useState<string | null>(linked ?? null);
  const close = () => {
    setOpen(null);
    if (linked !== undefined) onCloseLinked();
  };
  const userId = useQuery({ ...accessScopeQuery(), select: (scope) => scope.user_id }).data;
  // A chip's own read: the Studio's Image or Video My Generations, or its types, 5 a page.
  const jobs = useJobList(teamId, { status: "all", brandId, ...filter, pageSize: GENERATIONS_PAGE }, useStreamStatus());
  const catalog = useGetCatalog().data;
  const all = jobs.data?.pages.flatMap((each) => each.items);
  const shown = all?.filter((job) => who === "team" || job.created_by === userId);
  const readAgain = () => void jobs.refetch();
  return (
    <>
      <div className={classes.toolbar}>
        <SegmentedControl
          aria-label="Whose results"
          radius="xl"
          size="xs"
          classNames={{ root: create.seg, label: create.segLabel }}
          value={who}
          onChange={(value) => setWho(value === "team" ? "team" : "mine")}
          data={[
            { value: "mine", label: "Mine" },
            { value: "team", label: "Team" },
          ]}
        />
        {kinds}
      </div>
      {jobs.isError ? <ErrorAlert error={jobs.error} /> : null}
      {shown === undefined ? (
        <div className={`${create.gallery} ${classes.results}`}>
          {[0, 1, 2, 3].map((each) => (
            <Skeleton key={each} className={home.mediaSkeleton} />
          ))}
        </div>
      ) : shown.length === 0 && !(who === "mine" && jobs.hasNextPage) ? (
        <div className={create.empty}>
          <b>No results match</b>
          <span className={home.small}>Try another kind, or Team for everyone&apos;s.</span>
        </div>
      ) : (
        byDay(shown).map(({ day, jobs: dayJobs }) => (
          <section key={day} aria-label={day}>
            <h2 className={classes.day}>{day}</h2>
            <div className={`${create.gallery} ${classes.results}`}>
              {dayJobs.map((job) => (
                <ResultCard
                  key={job.id}
                  job={job}
                  model={modelTitle(catalog, job)}
                  onLost={readAgain}
                  onOpen={() => setOpen(job.id)}
                />
              ))}
            </div>
          </section>
        ))
      )}
      {who === "team" ? (
        // Live's: the next page as the end shows, no button.
        <LoadAhead more={morePages(jobs)} loaded={all?.length ?? 0} label="Loading more results" />
      ) : jobs.hasNextPage ? (
        // Mine keeps the user's own of the pages read: a button, so a team's many pages are
        // read only when asked for.
        <div className={classes.more}>
          {shown?.length === 0 ? <b>None of yours in the latest results</b> : null}
          <button
            type="button"
            className={home.smallButton}
            disabled={jobs.isFetchingNextPage}
            onClick={() => void jobs.fetchNextPage()}
          >
            Show more
          </button>
        </div>
      ) : null}
      {open === null ? null : <GenerationDetailModal teamId={teamId} jobId={open} onClose={close} />}
    </>
  );
}
